import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { SharedService } from './shared.service';
import * as fromRoot from '../../state/app.state';
import { CommonService } from '../services/common.service';
import { AuthService } from '../services/auth.service';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import {
  FetchAppDataFailure,
  FetchAppDataRequest,
  FetchAppDataSuccess,
  FetchSearchDetailsFailure,
  FetchSearchDetailsRequest,
  FetchSearchDetailsSuccess,
  FetchTranslationsFailure,
  FetchTranslationsRequest,
  FetchTranslationsSuccess,
  GetVideoByIdFailure,
  GetVideoByIdRequest,
  GetVideoByIdSuccess,
  GetVideoIdFailure,
  GetVideoIdRequest,
  GetVideoIdSuccess,
  UploadFileFailure,
  UploadFileRequest,
  UploadFileSuccess,
  UploadVideoFileFailure,
  UploadVideoFileRequest,
  UploadVideoFileSuccess,
} from './shared.actions';
import { SEARCH_ENTITIES } from '../constants/search-entities';
import { toAashtoWidgetConfig } from '../helpers/project-widget-config';

@Injectable()
export class SharedEffects {
  fetchAppData$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchAppDataRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(() =>
        this.sharedService.fetchAppData().pipe(
          map(response => {
            const { data } = response;
            data.workConditions = data?.workConditions?.map(o => ({
              value: o.code,
              label: o.text,
            }));

            data.weatherConditions = data?.weatherConditions?.map(o => ({
              ...o,
              value: o.code,
              label: o.text,
            }));

            data.offsetTypes = data?.offsetTypes?.map(o => ({
              value: o,
              label: o,
            }));

            // Org-level array only. A project override is renamed the same way
            // when it is resolved (see resolveWidgetConfig).
            if (data.enableAashtoIntegration && data.widgetConfig?.length) {
              data.widgetConfig = toAashtoWidgetConfig(data.widgetConfig);
            }

            if (data.dailyReportFields) {
              Object.keys(data.dailyReportFields).forEach(key => {
                const fields = data.dailyReportFields[key]?.fields;
                if (fields?.length && fields[0].display_order === 0) {
                  fields.forEach(f => f.display_order += 1);
                }
              });
            }

            data['genericPersonnel'] = data?.['genericPersonnel']?.map(gp => ({
              ...gp,
              value: gp.id,
              label: gp.name,
            })) || [];

            data['genericEquipment'] = data?.['genericEquipment']?.map(ge => ({
              ...ge,
              value: ge.id,
              label: [ge.name, ge.description].filter(Boolean).join(' - '),
            })) || [];

            if (data.abilities) {
              this.authService.storeFreshAbilities(data.abilities);
            }

            return FetchAppDataSuccess({ data });
          }),
          catchError((error) => {
            console.error('Failed to fetch app data:', error);
            return of(FetchAppDataFailure({ error }));
          }),
          tap((action: any) => {
            if (action.type === FetchAppDataSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchAppDataFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  uploadFile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UploadFileRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.sharedService.uploadFile(payload).pipe(
          map(response => {
            const { data } = response;
            data.forEach(eachItem => {

              if (eachItem.mime === 'image/jpeg' || eachItem.mime === 'image/png') {
                eachItem['fileType'] = 'picture';
              }

              if (eachItem?.mime?.startsWith('application/') || eachItem?.mime?.startsWith('text/')) {
                eachItem['fileType'] = 'document';
              }
            });
            return UploadFileSuccess({ data });
          }),
          catchError(() => {
            return of(UploadFileFailure());
          }),
          tap((action: any) => {
            if (action.type === UploadFileSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (payload.parentType === 'popup') {
                this.commonService.uploadedFilesInfoOnPopup.next(action.data);
              } else {
                this.commonService.uploadedFilesInfo.next(action.data);
              }
            } else if (action.type === UploadFileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  getVideoId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GetVideoIdRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.sharedService.getVideoId().pipe(
          map(response => {
            const { data } = response;
            const uploadVideoPayload = {
              ...data,
              formData: payload.videoFile,
              parentType: payload.parentType,
            };
            return GetVideoIdSuccess({ uploadVideoPayload });
          }),
          catchError(() => {
            return of(GetVideoIdFailure());
          }),
          tap((action: any) => {
            if (action.type === GetVideoIdSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.dispatch(UploadVideoFileRequest({ payload: action.uploadVideoPayload }));
            } else if (action.type === GetVideoIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  uploadVideoFile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UploadVideoFileRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.sharedService.uploadVideoFile(payload).pipe(
          map(() => {
            return UploadVideoFileSuccess();
          }),
          catchError(() => {
            return of(UploadVideoFileFailure());
          }),
          tap((action: any) => {
            if (action.type === UploadVideoFileSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.dispatch(GetVideoByIdRequest({ payload }));
            } else if (action.type === UploadVideoFileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  getVideoGetById$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GetVideoByIdRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.sharedService.getVideoById(payload).pipe(
          map(response => {
            const { data } = response;
            data['fileType'] = 'video';

            if (payload.parentType === 'popup') {
              this.commonService.uploadedFilesInfoOnPopup.next(data);
            } else {
              this.commonService.uploadedFilesInfo.next(data);
            }
            return GetVideoByIdSuccess();
          }),
          catchError(() => {
            return of(GetVideoByIdFailure());
          }),
          tap((action: any) => {
            if (action.type === GetVideoByIdSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === GetVideoByIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  fetchTranslations$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchTranslationsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap(payload =>
        this.sharedService.fetchTranslations(payload).pipe(
          map(response => {
            const { data } = response;
            const { translation } = data;
            const tempTranslations = JSON.parse(translation);
            return FetchTranslationsSuccess({
              translations: tempTranslations,
            });
          }),
          catchError(() => {
            return of(FetchTranslationsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchTranslationsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchTranslationsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          }),
        ),
      ),
    ),
  );

  fetchSearchDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSearchDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      // switchMap, not mergeMap: typing keeps firing requests and a slower
      // earlier one could land last, replacing the current query's results
      // (and stamping every row with the stale searchQuery the highlighter
      // uses). Cancel the previous request instead.
      switchMap(payload =>
        this.sharedService.fetchSearchDetails(payload).pipe(
          map(response => {
            // Registry-driven & null-safe: api master only returns
            // dailyReports/issues, the extended api adds the other keys.
            const resultsByType = {};
            let searchResults = [];

            SEARCH_ENTITIES.forEach(entity => {
              const rawResults = response?.[entity.apiKey] ?? [];
              const stampedResults = rawResults.map(result => {
                return {
                  ...result,
                  searchType: entity.searchType,
                  createdAt: result?.[entity.createdAtField],
                  score: result?._score,
                  // Query context for matched-field hints / highlighting.
                  searchQuery: payload?.searchText,
                };
              });

              stampedResults.sort((a, b) => b.score - a.score);

              resultsByType[entity.searchType] = stampedResults;
              searchResults = [...searchResults, ...stampedResults];
            });

            searchResults.sort((a, b) => b.score - a.score);

            return FetchSearchDetailsSuccess({
              searchResults,
              resultsByType,
              // The api caps each entity at a per-index window, so the arrays
              // are a page — this is how many records actually matched.
              totalRecords: response?.totalRecords ?? searchResults.length,
              // Legacy keys kept populated for existing consumers.
              dailyReports: resultsByType['dailyReport'] || [],
              issues: resultsByType['issues'] || [],
            });
          }),
          catchError(() => {
            return of(FetchSearchDetailsFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchSearchDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchSearchDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          }),
        ),
      ),
    ),
  );

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private sharedService: SharedService,
    private authService: AuthService,
    private actions: Actions,
  ) {
  }
}
